import { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import {
  ChefHat,
  LayoutDashboard,
  UtensilsCrossed,
  Settings,
  ShoppingBag,
  QrCode,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Bell,
  Copy,
  ExternalLink,
  Loader2,
  Image as ImageIcon,
  X,
  Volume2,
  BellRing,
  Eye,
  EyeOff,
  Check,
  BarChart3,
  TrendingUp,
  Users,
  Upload
} from "lucide-react";

const API = `${window.location.origin}/api`;

// Notification sound URL
const NOTIFICATION_SOUND_URL = "https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3";

// ── Web Audio API for background tab support ──
let audioCtx = null;
let notifBuffer = null;

const ensureAudioContext = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === "suspended") {
    audioCtx.resume();
  }
  return audioCtx;
};

// Pre-fetch and decode notification sound
const preloadNotificationSound = async () => {
  try {
    const ctx = ensureAudioContext();
    const resp = await fetch(NOTIFICATION_SOUND_URL);
    const arrayBuf = await resp.arrayBuffer();
    notifBuffer = await ctx.decodeAudioData(arrayBuf);
  } catch (e) {
    console.warn("Notification sound preload failed:", e);
  }
};

// Request notification permission
const requestNotificationPermission = async () => {
  if ("Notification" in window && Notification.permission === "default") {
    await Notification.requestPermission();
  }
};

// Show desktop notification - unique tag per call so multiple can appear
const showDesktopNotification = (title, body, icon) => {
  if ("Notification" in window && Notification.permission === "granted") {
    const notification = new Notification(title, {
      body,
      icon: icon || "/favicon.ico",
      badge: "/favicon.ico",
      tag: `order-${Date.now()}`,
      renotify: true,
      requireInteraction: true,
      vibrate: [200, 100, 200, 100, 200],
      silent: false
    });
    notification.onclick = () => {
      window.focus();
      // Navigate to dashboard if not already there
      if (!window.location.pathname.startsWith('/dashboard')) {
        window.location.href = '/dashboard/orders';
      }
      notification.close();
    };
  }
};

// Play loud notification sound via Web Audio API (works in background tabs)
const playNotificationSound = () => {
  try {
    const ctx = ensureAudioContext();

    const playOnce = () => {
      if (notifBuffer && ctx) {
        const src = ctx.createBufferSource();
        const gain = ctx.createGain();
        src.buffer = notifBuffer;
        gain.gain.value = 1.0;
        src.connect(gain).connect(ctx.destination);
        src.start(0);
        return src;
      }
      // Fallback to Audio element if buffer not loaded
      const a = new Audio(NOTIFICATION_SOUND_URL);
      a.volume = 1.0;
      a.play().catch(() => {});
      return a;
    };

    // Play 3 times with 1.5s gap
    playOnce();
    setTimeout(playOnce, 1500);
    setTimeout(playOnce, 3000);
  } catch (e) {
    console.warn("Notification sound play error:", e);
  }
};

// ─── QR Table Generator Component ─────────────────────────
const QRTableGenerator = ({ menuUrl, restaurant }) => {
  const { token } = useAuth();
  const [qrCodes, setQrCodes] = useState([]);
  const [loadingQR, setLoadingQR] = useState(true);
  const [bulkPrefix, setBulkPrefix] = useState("");
  const [bulkCount, setBulkCount] = useState(10);
  const [bulkStart, setBulkStart] = useState(1);
  const [manualLabel, setManualLabel] = useState("");
  const [editingQR, setEditingQR] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const fetchQR = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/qr-codes`, { headers: { Authorization: `Bearer ${token}` } });
      setQrCodes(res.data || []);
    } catch {} finally { setLoadingQR(false); }
  }, [token]);

  useEffect(() => { fetchQR(); }, [fetchQR]);

  const handleBulkCreate = async () => {
    try {
      await axios.post(`${API}/qr-codes/bulk`, { prefix: bulkPrefix, count: bulkCount, start: bulkStart }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${bulkCount} QR kod oluşturuldu!`);
      fetchQR();
    } catch { toast.error("Oluşturulamadı"); }
  };

  const handleManualAdd = async () => {
    if (!manualLabel.trim()) return;
    try {
      await axios.post(`${API}/qr-codes`, { label: manualLabel.trim(), prefix: "" }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("QR kod eklendi!");
      setManualLabel("");
      fetchQR();
    } catch { toast.error("Eklenemedi"); }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`${API}/qr-codes/${id}`, { label: editLabel, prefix: "" }, { headers: { Authorization: `Bearer ${token}` } });
      setEditingQR(null);
      fetchQR();
    } catch { toast.error("Güncellenemedi"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/qr-codes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchQR();
    } catch { toast.error("Silinemedi"); }
  };

  const printAll = () => {
    if (qrCodes.length === 0) { toast.error("Yazdırılacak QR kod yok"); return; }
    const w = window.open("", "_blank");
    if (!w) return;
    const logoUrl = restaurant?.logo_url || "";
    const pc = restaurant?.primary_color || "#f97316";
    let html = `<html><head><title>${restaurant?.name} - QR Kodları</title>
      <style>
        *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}
        body{font-family:'Segoe UI',sans-serif;margin:0;padding:20px;background:#f8f9fa}
        .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:24px}
        .card{border:2px solid #e5e7eb;border-radius:20px;padding:28px 20px 20px;text-align:center;page-break-inside:avoid;background:white;position:relative}
        .card .logo{width:40px;height:40px;border-radius:10px;object-fit:cover;position:absolute;top:-20px;left:50%;transform:translateX(-50%);border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.15)}
        .card .logo-placeholder{width:40px;height:40px;border-radius:10px;position:absolute;top:-20px;left:50%;transform:translateX(-50%);border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.15);background:${pc}!important;display:flex;align-items:center;justify-content:center;color:white!important;font-weight:bold;font-size:18px}
        .card h3{margin:8px 0 2px;font-size:24px;font-weight:700;color:#000!important}
        .card .rname{margin:0 0 4px;color:${pc}!important;font-size:12px;font-weight:600}
        @media print{.grid{grid-template-columns:repeat(2,1fr)}.no-print{display:none}body{background:white!important}}
      </style>
      <script>
        function updateSize(val) {
          document.getElementById('sizeLabel').textContent = val + 'px';
          document.querySelectorAll('.qr-img').forEach(img => {
            img.width = val; img.height = val;
          });
        }
      </script>
      </head><body>
      <div class="no-print" style="text-align:center;margin-bottom:24px;display:flex;flex-direction:column;align-items:center;gap:14px">
        <div style="display:flex;align-items:center;gap:12px;background:#fff;padding:10px 24px;border-radius:12px;box-shadow:0 2px 8px rgba(0,0,0,0.08)">
          <span style="font-size:13px;font-weight:600;color:#374151">QR Boyutu:</span>
          <input type="range" min="120" max="400" value="220" step="10" oninput="updateSize(this.value)" style="width:180px;accent-color:${pc}" />
          <span id="sizeLabel" style="font-size:13px;font-weight:700;color:${pc};min-width:50px">220px</span>
        </div>
        <button onclick="window.print()" style="padding:14px 40px;font-size:16px;background:${pc};color:white;border:none;border-radius:12px;cursor:pointer;font-weight:600">Yazdır / PDF</button>
      </div><div class="grid">`;
    qrCodes.forEach((qr) => {
      const url = `${menuUrl}?masa=${encodeURIComponent(qr.label)}`;
      html += `<div class="card">
        ${logoUrl ? `<img class="logo" src="${logoUrl}" />` : `<div class="logo-placeholder">${(restaurant?.name || "M")[0]}</div>`}
        <img class="qr-img" src="https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(url)}&margin=8" width="220" height="220" style="border-radius:12px" />
        <h3>${qr.label}</h3>
        <p class="rname">${restaurant?.name}</p>
        <div style="margin-top:10px;padding:10px 0;background-color:${pc}!important;color:#ffffff!important;border-radius:10px;font-weight:800;font-size:14px;letter-spacing:0.5px;-webkit-print-color-adjust:exact;print-color-adjust:exact;border:2px solid ${pc}">SİPARİŞ İÇİN QR KODU OKUTUN</div>
      </div>`;
    });
    html += `</div></body></html>`;
    w.document.write(html);
    w.document.close();
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <QrCode className="w-5 h-5 text-emerald-500" />
          Masa QR Kodları
          {qrCodes.length > 0 && <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">{qrCodes.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Bulk Create */}
        <div className="bg-slate-800/50 rounded-xl p-4 space-y-3">
          <Label className="text-slate-300 text-sm font-medium">Toplu Oluştur</Label>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-slate-500 text-xs">Ön Ek (opsiyonel)</Label>
              <Input value={bulkPrefix} onChange={(e) => setBulkPrefix(e.target.value)} placeholder="A, B, VIP..."
                className="bg-slate-800 border-slate-700 text-white h-9" data-testid="qr-prefix-input" />
            </div>
            <div>
              <Label className="text-slate-500 text-xs">Başlangıç No</Label>
              <Input type="number" min={1} value={bulkStart} onChange={(e) => setBulkStart(parseInt(e.target.value) || 1)}
                className="bg-slate-800 border-slate-700 text-white h-9" data-testid="qr-start-input" />
            </div>
            <div>
              <Label className="text-slate-500 text-xs">Adet</Label>
              <Input type="number" min={1} max={200} value={bulkCount} onChange={(e) => setBulkCount(Math.min(200, parseInt(e.target.value) || 1))}
                className="bg-slate-800 border-slate-700 text-white h-9" data-testid="qr-count-input" />
            </div>
          </div>
          <p className="text-slate-500 text-xs">Örnek: Ön ek "A" + Başlangıç 1 + Adet 5 → A1, A2, A3, A4, A5</p>
          <Button onClick={handleBulkCreate} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="bulk-create-qr-btn">
            <QrCode className="w-4 h-4 mr-2" /> Toplu Oluştur
          </Button>
        </div>

        {/* Manual Add */}
        <div className="flex gap-2">
          <Input value={manualLabel} onChange={(e) => setManualLabel(e.target.value)} placeholder="Manuel ekle: Bahçe 1, Teras 3..."
            className="bg-slate-800 border-slate-700 text-white h-9 flex-1" data-testid="qr-manual-input"
            onKeyDown={(e) => e.key === "Enter" && handleManualAdd()} />
          <Button onClick={handleManualAdd} size="sm" className="bg-blue-600 hover:bg-blue-700 h-9" data-testid="manual-add-qr-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Saved QR List */}
        {loadingQR ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
        ) : qrCodes.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-4">Henüz QR kod oluşturulmadı</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="qr-codes-grid">
              {qrCodes.map((qr) => (
                <div key={qr.id} className="bg-white p-3 rounded-xl text-center relative pt-6 group" data-testid={`qr-${qr.id}`}>
                  {restaurant?.logo_url ? (
                    <img src={restaurant.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover absolute -top-3 left-1/2 -translate-x-1/2 border-2 border-white shadow-sm" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg absolute -top-3 left-1/2 -translate-x-1/2 border-2 border-white shadow-sm flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: restaurant?.primary_color || "#f97316" }}>{(restaurant?.name || "M")[0]}</div>
                  )}
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <button onClick={() => { setEditingQR(qr.id); setEditLabel(qr.label); }}
                      className="w-5 h-5 rounded bg-blue-500 text-white flex items-center justify-center" data-testid={`edit-qr-${qr.id}`}>
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleDelete(qr.id)}
                      className="w-5 h-5 rounded bg-red-500 text-white flex items-center justify-center" data-testid={`delete-qr-${qr.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <QRCodeSVG value={`${menuUrl}?masa=${encodeURIComponent(qr.label)}`} size={80} />
                  {editingQR === qr.id ? (
                    <div className="mt-1 flex gap-1">
                      <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
                        className="h-6 text-xs px-1 border-blue-400" autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") handleUpdate(qr.id); if (e.key === "Escape") setEditingQR(null); }} />
                      <button onClick={() => handleUpdate(qr.id)} className="text-blue-500"><Check className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <p className="text-xs font-bold mt-1 text-stone-700 truncate">{qr.label}</p>
                  )}
                  <p className="text-[8px] font-bold mt-1 text-white px-1.5 py-0.5 rounded" style={{ background: restaurant?.primary_color || "#f97316" }}>SİPARİŞ İÇİN QR KODU OKUTUN</p>
                </div>
              ))}
            </div>
            <Button onClick={printAll} className="w-full bg-emerald-600 hover:bg-emerald-700 mt-3" data-testid="print-all-qr-btn">
              <QrCode className="w-4 h-4 mr-2" /> Tümünü Yazdır ({qrCodes.length} QR)
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const Dashboard = () => {
  const { token, restaurant, logout, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [showOrderAlert, setShowOrderAlert] = useState(false);
  const [newOrderDetails, setNewOrderDetails] = useState(null);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [showNotifBanner, setShowNotifBanner] = useState(false);
  const [waiterCalls, setWaiterCalls] = useState([]);
  const [showWaiterAlert, setShowWaiterAlert] = useState(false);
  const [billRequests, setBillRequests] = useState([]);
  const [showBillAlert, setShowBillAlert] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const workerRef = useRef(null);
  const prevCountRef = useRef(-1);
  const orderAcknowledgedRef = useRef(false);

  // Check notification permission on mount
  useEffect(() => {
    if ("Notification" in window) {
      if (Notification.permission === "granted") {
        setNotificationEnabled(true);
        ensureAudioContext();
        preloadNotificationSound();
      } else if (Notification.permission === "default") {
        setShowNotifBanner(true);
      }
    }
  }, []);

  // Initialize AudioContext on first user interaction (critical for mobile/Android)
  useEffect(() => {
    const initAudioOnGesture = () => {
      ensureAudioContext();
      preloadNotificationSound();
      document.removeEventListener('click', initAudioOnGesture);
      document.removeEventListener('touchstart', initAudioOnGesture);
    };
    document.addEventListener('click', initAudioOnGesture, { once: true });
    document.addEventListener('touchstart', initAudioOnGesture, { once: true });
    return () => {
      document.removeEventListener('click', initAudioOnGesture);
      document.removeEventListener('touchstart', initAudioOnGesture);
    };
  }, []);

  // Handler for notification enable button (user gesture required)
  const handleEnableNotifications = async () => {
    try {
      const result = await Notification.requestPermission();
      if (result === "granted") {
        setNotificationEnabled(true);
        setShowNotifBanner(false);
        ensureAudioContext();
        preloadNotificationSound();
        toast.success("Bildirimler açıldı! Siparişlerden haberdar olacaksınız.");
      } else {
        setShowNotifBanner(false);
        toast.error("Bildirimler reddedildi. Tarayıcı ayarlarından açabilirsiniz.");
      }
    } catch {
      setShowNotifBanner(false);
    }
  };

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [loading, isAuthenticated, navigate]);

  // Poll for new orders
  // ─── Web Worker for background-safe polling ───
  useEffect(() => {
    if (!token) return;

    // Create Web Worker - runs at full speed even when tab is in background
    const worker = new Worker('/notification-worker.js');
    workerRef.current = worker;

    worker.onmessage = (e) => {
      const { type, newCount, totalCount, calls, newCall, requests, newRequest } = e.data;

      if (type === 'NEW_ORDER') {
        orderAcknowledgedRef.current = false;
        ensureAudioContext();
        playNotificationSound();
        showDesktopNotification(
          "YENİ SİPARİŞ!",
          `${newCount} yeni sipariş geldi! Toplam ${totalCount} bekleyen sipariş.`,
          restaurant?.logo_url
        );
        setNewOrderDetails({ count: newCount, total: totalCount });
        setShowOrderAlert(true);
        setNewOrdersCount(totalCount);
        toast.success("YENİ SİPARİŞ GELDİ!", {
          description: `${newCount} yeni sipariş bekliyor`,
          duration: 10000
        });
      }

      if (type === 'WAITER_CALL') {
        ensureAudioContext();
        playNotificationSound();
        showDesktopNotification(
          "GARSON ÇAĞRISI!",
          `Masa ${newCall?.table_number || '?'} garson istiyor!`,
          restaurant?.logo_url
        );
        setShowWaiterAlert(true);
      }

      if (type === 'WAITER_STATE') {
        setWaiterCalls(calls || []);
      }

      if (type === 'BILL_REQUEST') {
        ensureAudioContext();
        playNotificationSound();
        showDesktopNotification(
          "HESAP İSTEĞİ!",
          `Masa ${newRequest?.table_number || '?'} hesap istiyor! (${newRequest?.payment_method || ''})`,
          restaurant?.logo_url
        );
        setShowBillAlert(true);
      }

      if (type === 'BILL_STATE') {
        setBillRequests(requests || []);
      }
    };

    // Start worker with API URL and token
    worker.postMessage({
      action: 'START',
      data: { apiUrl: API, token, interval: 6000 }
    });

    return () => {
      worker.postMessage({ action: 'STOP' });
      worker.terminate();
      workerRef.current = null;
    };
  }, [token, restaurant]);

  const dismissWaiterCall = async (callId) => {
    try {
      await axios.put(`${API}/waiter-calls/${callId}/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setWaiterCalls(prev => {
        const updated = prev.filter(c => c.id !== callId);
        workerRef.current?.postMessage({ action: 'RESET_COUNTS', data: { type: 'waiter', count: updated.length } });
        if (updated.length === 0) setShowWaiterAlert(false);
        return updated;
      });
    } catch {}
  };

  const dismissBillRequest = async (reqId) => {
    try {
      await axios.put(`${API}/bill-requests/${reqId}/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setBillRequests(prev => {
        const updated = prev.filter(r => r.id !== reqId);
        workerRef.current?.postMessage({ action: 'RESET_COUNTS', data: { type: 'bill', count: updated.length } });
        if (updated.length === 0) setShowBillAlert(false);
        return updated;
      });
    } catch {}
  };

  // Enable notification permission handler
  const enableNotifications = async () => {
    const permission = await Notification.requestPermission();
    setNotificationEnabled(permission === "granted");
    if (permission === "granted") {
      // Initialize AudioContext on user gesture (required by browsers)
      ensureAudioContext();
      await preloadNotificationSound();
      // Play a test sound so user hears it works
      playNotificationSound();
      toast.success("Bildirimler etkinleştirildi!");
    }
  };

  // 30-second reminder for unacknowledged pending orders
  useEffect(() => {
    if (!showOrderAlert && newOrdersCount > 0 && !orderAcknowledgedRef.current) {
      const reminder = setInterval(() => {
        if (newOrdersCount > 0 && !orderAcknowledgedRef.current) {
          ensureAudioContext();
          playNotificationSound();
          showDesktopNotification(
            "HATIRLATMA: Bekleyen Sipariş!",
            `${newOrdersCount} sipariş hala bekliyor!`,
            restaurant?.logo_url
          );
          setShowOrderAlert(true);
        }
      }, 30000);
      return () => clearInterval(reminder);
    }
  }, [showOrderAlert, newOrdersCount, restaurant]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const navItems = [
    { to: "/dashboard", icon: <LayoutDashboard className="w-5 h-5" />, label: "Genel Bakış", end: true },
    { to: "/dashboard/menu", icon: <UtensilsCrossed className="w-5 h-5" />, label: "Menü Yönetimi" },
    { to: "/dashboard/orders", icon: <ShoppingBag className="w-5 h-5" />, label: "Siparişler", badge: newOrdersCount },
    { to: "/dashboard/statistics", icon: <BarChart3 className="w-5 h-5" />, label: "İstatistikler" },
    { to: "/dashboard/settings", icon: <Settings className="w-5 h-5" />, label: "Ayarlar" }
  ];

  return (
    <div className="min-h-screen bg-slate-950 dark flex">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 py-3">
        <button onClick={() => setMobileMenuOpen(true)} className="text-slate-300 hover:text-white" data-testid="mobile-menu-btn">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
            <ChefHat className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-semibold text-sm">{restaurant?.name || "MenuAI"}</span>
        </div>
        <div className="w-6" />
      </div>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-[60] bg-black/60" onClick={() => setMobileMenuOpen(false)} />
        )}
      </AnimatePresence>

      {/* Sidebar - desktop fixed, mobile slide */}
      <aside className={`w-64 bg-slate-900 border-r border-slate-800 flex flex-col fixed h-full z-[70] transition-transform duration-300 md:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-semibold font-['Manrope']">{restaurant?.name || "MenuAI"}</h1>
              <p className="text-slate-500 text-xs">Dashboard</p>
            </div>
          </div>
          <button onClick={() => setMobileMenuOpen(false)} className="md:hidden text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? "active" : "text-slate-400"}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {item.icon}
              <span>{item.label}</span>
              {item.badge > 0 && (
                <Badge className="ml-auto bg-orange-500 text-white badge-pulse">
                  {item.badge}
                </Badge>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <Button
            variant="ghost"
            onClick={() => { logout(); navigate("/"); }}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Notification Permission Banner */}
        {showNotifBanner && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 rounded-xl flex items-center justify-between gap-3"
            style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)', boxShadow: '0 4px 16px rgba(249,115,22,0.3)' }}
            data-testid="notification-banner">
            <div className="flex items-center gap-2.5 min-w-0">
              <Bell className="w-5 h-5 text-white flex-shrink-0" />
              <p className="text-white text-sm font-medium">Sipariş bildirimlerini açın - arka planda bile haberdar olun!</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button onClick={handleEnableNotifications} size="sm" className="bg-white text-orange-600 hover:bg-orange-50 font-bold text-xs px-4" data-testid="enable-notifications-btn">
                Bildirimleri Aç
              </Button>
              <button onClick={() => setShowNotifBanner(false)} className="text-white/70 hover:text-white text-lg leading-none">&times;</button>
            </div>
          </motion.div>
        )}
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="menu" element={<MenuManagement />} />
          <Route path="orders" element={<OrderManagement />} />
          <Route path="statistics" element={<StatisticsPage />} />
          <Route path="settings" element={<RestaurantSettings />} />
        </Routes>
      </main>

      {/* NEW ORDER ALERT MODAL - Full Screen Overlay */}
      <AnimatePresence>
        {showOrderAlert && newOrderDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
            onClick={() => setShowOrderAlert(false)}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="bg-gradient-to-br from-orange-500 to-red-600 p-1 rounded-3xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-slate-900 rounded-3xl p-8 text-center max-w-md">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="w-24 h-24 mx-auto mb-6 rounded-full bg-orange-500/20 flex items-center justify-center"
                >
                  <BellRing className="w-12 h-12 text-orange-500" />
                </motion.div>
                
                <h2 className="text-3xl font-bold text-white font-['Manrope'] mb-2">
                  🔔 YENİ SİPARİŞ!
                </h2>
                
                <p className="text-6xl font-bold text-orange-500 my-4">
                  {newOrderDetails.count}
                </p>
                
                <p className="text-slate-400 text-lg mb-6">
                  yeni sipariş geldi!<br/>
                  Toplam <span className="text-white font-semibold">{newOrderDetails.total}</span> bekleyen sipariş
                </p>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      orderAcknowledgedRef.current = true;
                      setShowOrderAlert(false);
                      navigate("/dashboard/orders");
                    }}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg rounded-xl"
                  >
                    Siparişlere Git
                  </Button>
                  <Button
                    onClick={() => {
                      orderAcknowledgedRef.current = true;
                      setShowOrderAlert(false);
                    }}
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 py-6 rounded-xl"
                  >
                    Kapat
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WAITER CALL ALERT MODAL */}
      <AnimatePresence>
        {showWaiterAlert && waiterCalls.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-black/85 flex items-center justify-center"
            onClick={() => setShowWaiterAlert(false)}>
            <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="bg-gradient-to-br from-amber-500 to-yellow-600 p-1 rounded-3xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}>
              <div className="bg-slate-900 rounded-3xl p-8 text-center max-w-md">
                <motion.div animate={{ rotate: [0, -15, 15, -15, 0] }} transition={{ repeat: Infinity, duration: 0.5 }}
                  className="w-20 h-20 mx-auto mb-5 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Bell className="w-10 h-10 text-amber-500" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white mb-4">GARSON ÇAĞRISI!</h2>
                <div className="space-y-2 mb-6">
                  {waiterCalls.map((call) => (
                    <div key={call.id} className="bg-slate-800 rounded-xl p-3 flex items-center justify-between" data-testid={`waiter-call-${call.id}`}>
                      <div>
                        <span className="text-amber-400 font-bold text-lg">Masa {call.table_number || "?"}</span>
                        <span className="text-slate-500 text-xs ml-2">garson istiyor</span>
                      </div>
                      <Button size="sm" onClick={() => dismissWaiterCall(call.id)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`dismiss-call-${call.id}`}>
                        <Check className="w-3 h-3 mr-1" /> Tamam
                      </Button>
                    </div>
                  ))}
                </div>
                <Button onClick={() => setShowWaiterAlert(false)} variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 rounded-xl">Kapat</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bill Request Alert */}
      <AnimatePresence>
        {showBillAlert && billRequests.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9997] bg-black/85 flex items-center justify-center"
            onClick={() => setShowBillAlert(false)}>
            <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="bg-gradient-to-br from-blue-500 to-indigo-600 p-1 rounded-3xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}>
              <div className="bg-slate-900 rounded-3xl p-8 text-center max-w-md">
                <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M7 15h0M2 9.5h20"/></svg>
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">HESAP İSTEĞİ!</h2>
                <div className="space-y-2 mb-6">
                  {billRequests.map((req) => (
                    <div key={req.id} className="bg-slate-800 rounded-xl p-3 flex items-center justify-between" data-testid={`bill-req-${req.id}`}>
                      <div>
                        <span className="text-blue-400 font-bold text-lg">Masa {req.table_number || "?"}</span>
                        <span className="text-slate-500 text-xs ml-2">hesap istiyor {req.payment_type === 'card' ? '(Kart)' : req.payment_type === 'cash' ? '(Nakit)' : ''}</span>
                      </div>
                      <Button size="sm" onClick={() => dismissBillRequest(req.id)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid={`dismiss-bill-${req.id}`}>
                        <Check className="w-3 h-3 mr-1" /> Tamam
                      </Button>
                    </div>
                  ))}
                </div>
                <Button onClick={() => setShowBillAlert(false)} variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800 rounded-xl">Kapat</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification Permission Banner */}
      {!notificationEnabled && "Notification" in window && (
        <div className="fixed bottom-4 right-4 z-50">
          <Card className="bg-slate-800 border-slate-700 shadow-xl">
            <CardContent className="p-4 flex items-center gap-4">
              <Bell className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-white font-medium">Bildirimleri Etkinleştir</p>
                <p className="text-slate-400 text-sm">Yeni siparişlerden anında haberdar ol</p>
              </div>
              <Button
                onClick={enableNotifications}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Volume2 className="w-4 h-4 mr-2" />
                Etkinleştir
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Dashboard Overview Component
const DashboardOverview = () => {
  const { restaurant } = useAuth();
  const menuUrl = `${window.location.origin}/menu/${restaurant?.slug}`;

  const copyLink = () => {
    navigator.clipboard.writeText(menuUrl);
    toast.success("Link kopyalandı!");
  };

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      <div>
        <h1 className="text-3xl font-bold text-white font-['Manrope']">Hoş Geldiniz!</h1>
        <p className="text-slate-400 mt-1">Restoranınızın genel durumunu buradan takip edebilirsiniz.</p>
      </div>

      {/* QR Code Card - Per Table */}
      <QRTableGenerator menuUrl={menuUrl} restaurant={restaurant} />

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card">
          <p className="text-slate-400 text-sm">Menü Linki</p>
          <p className="text-2xl font-bold text-white mt-1">/{restaurant?.slug}</p>
        </div>
        <div className="stat-card">
          <p className="text-slate-400 text-sm">Durum</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <p className="text-xl font-bold text-emerald-500">Aktif</p>
          </div>
        </div>
        <div className="stat-card">
          <p className="text-slate-400 text-sm">AI Asistan</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <p className="text-xl font-bold text-emerald-500">Hazır</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Menu Management Component
const MenuManagement = () => {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", order: 0, image_url: "", title_alignment: "left", parent_id: "" });
  const [itemForm, setItemForm] = useState({
    category_id: "", 
    subcategory_id: "",
    name: "", 
    description: "",
    long_description: "",
    price: "", 
    image_url: "" 
  });

  // Menu photo import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importAnalyzing, setImportAnalyzing] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const menuPhotoRef = useRef(null);
  const [aiImageLoading, setAiImageLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bulkGenProgress, setBulkGenProgress] = useState(null); // {current, total} or null

  const generateAiImage = async (productName, productDesc, categoryName, setter) => {
    if (!productName || !productName.trim()) {
      toast.error("Lütfen önce adı doldurun!");
      return;
    }
    setAiImageLoading(true);
    try {
      const parts = [`professional food photography of ${productName}`];
      if (productDesc) parts.push(productDesc);
      if (categoryName) parts.push(`category: ${categoryName}`);
      parts.push("on a plate, restaurant style, high quality, appetizing, soft lighting");
      const prompt = parts.join(", ");
      const res = await axios.post(`${API}/ai/generate-image`, { prompt }, { timeout: 180000 });
      if (res.data?.image_base64) {
        const dataUrl = `data:image/png;base64,${res.data.image_base64}`;
        setter(dataUrl);
        toast.success("AI görsel oluşturuldu!");
      }
    } catch (err) {
      toast.error("Görsel oluşturulamadı, tekrar deneyin.");
    } finally {
      setAiImageLoading(false);
    }
  };

  const bulkGenerateImages = async () => {
    const itemsWithoutImage = items.filter(i => !i.image_url);
    const catsWithoutImage = categories.filter(c => !c.image_url);
    const totalCount = itemsWithoutImage.length + catsWithoutImage.length;
    if (totalCount === 0) {
      toast.info("Tüm ürün ve kategorilerin görseli mevcut!");
      return;
    }
    if (!window.confirm(`${catsWithoutImage.length} kategori + ${itemsWithoutImage.length} ürün için AI görsel oluşturulacak. Bu işlem biraz zaman alabilir. Devam etmek istiyor musunuz?`)) return;
    
    setBulkGenProgress({ current: 0, total: totalCount });
    let success = 0;
    let idx = 0;

    // Generate for categories first
    for (const cat of catsWithoutImage) {
      idx++;
      setBulkGenProgress({ current: idx, total: totalCount });
      try {
        const prompt = `professional food photography representing ${cat.name} category, restaurant style, high quality, appetizing, soft lighting`;
        const res = await axios.post(`${API}/ai/generate-image`, { prompt }, { timeout: 180000 });
        if (res.data?.image_base64) {
          const dataUrl = `data:image/png;base64,${res.data.image_base64}`;
          await axios.put(`${API}/menu/categories/${cat.id}`, { ...cat, image_url: dataUrl }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          success++;
        }
      } catch {}
    }

    // Then generate for items
    for (const item of itemsWithoutImage) {
      idx++;
      setBulkGenProgress({ current: idx, total: totalCount });
      try {
        const cat = categories.find(c => c.id === item.category_id);
        const parts = [`professional food photography of ${item.name}`];
        if (item.description) parts.push(item.description);
        if (cat?.name) parts.push(`category: ${cat.name}`);
        parts.push("on a plate, restaurant style, high quality, appetizing, soft lighting");
        const res = await axios.post(`${API}/ai/generate-image`, { prompt: parts.join(", ") }, { timeout: 180000 });
        if (res.data?.image_base64) {
          const dataUrl = `data:image/png;base64,${res.data.image_base64}`;
          await axios.put(`${API}/menu/items/${item.id}`, { ...item, image_url: dataUrl, price: item.price }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          success++;
        }
      } catch {}
    }

    setBulkGenProgress(null);
    fetchData();
    toast.success(`${success}/${totalCount} görsel oluşturuldu!`);
  };

  const handleMenuPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      setImportAnalyzing(true);
      setShowImportDialog(true);
      setImportResults(null);
      try {
        const res = await axios.post(`${API}/ai/analyze-menu`, { image_base64: base64 }, {
          headers: { Authorization: `Bearer ${token}` }, timeout: 60000
        });
        setImportResults(res.data.items || []);
      } catch {
        toast.error("Menü analizi başarısız oldu");
        setImportResults([]);
      } finally {
        setImportAnalyzing(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleBulkImport = async () => {
    if (!importResults || importResults.length === 0) return;
    setImportSubmitting(true);
    try {
      const res = await axios.post(`${API}/ai/bulk-import`, { items: importResults }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.created_items} ürün ve ${res.data.created_categories} kategori oluşturuldu!`);
      setShowImportDialog(false);
      setImportResults(null);
      fetchData();
    } catch {
      toast.error("İçe aktarma başarısız");
    } finally {
      setImportSubmitting(false);
    }
  };

  const removeImportItem = (idx) => {
    setImportResults(prev => prev.filter((_, i) => i !== idx));
  };

  const fetchData = useCallback(async () => {
    try {
      const [catRes, itemRes] = await Promise.all([
        axios.get(`${API}/menu/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/menu/items`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCategories(catRes.data);
      setItems(itemRes.data);
    } catch (error) {
      toast.error("Menü verileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/menu/categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Kategori güncellendi");
      } else {
        await axios.post(`${API}/menu/categories`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Kategori oluşturuldu");
      }
      setCategoryDialog(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", order: 0, image_url: "", title_alignment: "left", parent_id: "" });
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Bu kategoriyi silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/menu/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Kategori silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleSaveItem = async () => {
    try {
      const data = { ...itemForm, price: parseFloat(itemForm.price) };
      if (editingItem) {
        await axios.put(`${API}/menu/items/${editingItem.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Ürün güncellendi");
      } else {
        await axios.post(`${API}/menu/items`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Ürün oluşturuldu");
      }
      setItemDialog(false);
      setEditingItem(null);
      setItemForm({ category_id: "", subcategory_id: "", name: "", description: "", long_description: "", price: "", image_url: "" });
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/menu/items/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ürün silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleToggleVisibility = async (id) => {
    try {
      await axios.put(`${API}/menu/items/${id}/visibility`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      toast.error("Görünürlük değiştirilemedi");
    }
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name, order: cat.order, image_url: cat.image_url || "", title_alignment: cat.title_alignment || "left", parent_id: cat.parent_id || "" });
    setCategoryDialog(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      category_id: item.category_id,
      subcategory_id: item.subcategory_id || "",
      name: item.name,
      description: item.description || "",
      long_description: item.long_description || "",
      price: item.price.toString(),
      image_url: item.image_url || ""
    });
    setItemDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="menu-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Manrope']">Menü Yönetimi</h1>
          <p className="text-slate-400 mt-1">Kategorilerinizi ve ürünlerinizi buradan yönetin.</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => { setEditingCategory(null); setCategoryForm({ name: "", order: 0, image_url: "", title_alignment: "left" }); }}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="add-category-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Kategori Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingCategory ? "Kategori Düzenle" : "Yeni Kategori"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Kategori Adı</Label>
                  <Input
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="Örn: Ana Yemekler"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="category-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Üst Kategori (Alt kategori için)</Label>
                  <select
                    value={categoryForm.parent_id}
                    onChange={(e) => setCategoryForm({ ...categoryForm, parent_id: e.target.value })}
                    className="w-full rounded-md bg-slate-800 border-slate-700 text-white px-3 py-2 text-sm"
                    data-testid="category-parent-select"
                  >
                    <option value="">Ana Kategori (Üst düzey)</option>
                    {categories.filter(c => !c.parent_id && c.id !== editingCategory?.id).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <p className="text-slate-500 text-xs">Boş bırakırsanız ana kategori olur. Bir üst kategori seçerseniz alt kategori olarak eklenir.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Sıralama</Label>
                  <Input
                    type="number"
                    value={categoryForm.order}
                    onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="category-order-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Arkaplan Görseli (URL)</Label>
                  <Input
                    value={categoryForm.image_url}
                    onChange={(e) => setCategoryForm({ ...categoryForm, image_url: e.target.value })}
                    placeholder="https://..."
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="category-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full border-purple-500/40 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200 gap-2"
                    disabled={aiImageLoading}
                    onClick={() => {
                      generateAiImage(categoryForm.name, null, null, (url) => setCategoryForm(prev => ({ ...prev, image_url: url })));
                    }}
                    data-testid="ai-generate-category-image-btn"
                  >
                    {aiImageLoading ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Oluşturuluyor...</>
                    ) : (
                      <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 0-4 4c0 2 2 3 2 6H6a2 2 0 0 0-2 2v2h16v-2a2 2 0 0 0-2-2h-4c0-3 2-4 2-6a4 4 0 0 0-4-4z"/><path d="M8 18v2a2 2 0 0 0 4 0v-2"/></svg> AI ile Görsel Oluştur</>
                    )}
                  </Button>
                  {categoryForm.image_url && (
                    <div className="relative w-full h-24 rounded-lg overflow-hidden mt-2">
                      <img src={categoryForm.image_url} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">Önizleme</span>
                      </div>
                    </div>
                  )}
                </div>
                <Button onClick={handleSaveCategory} className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="save-category-btn">
                  {editingCategory ? "Güncelle" : "Oluştur"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={itemDialog} onOpenChange={setItemDialog}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => { setEditingItem(null); setItemForm({ category_id: "", name: "", description: "", price: "", image_url: "" }); }}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="add-item-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ürün Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingItem ? "Ürün Düzenle" : "Yeni Ürün"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                <div className="space-y-2">
                  <Label className="text-slate-300">Kategori</Label>
                  <Select value={itemForm.category_id} onValueChange={(v) => setItemForm({ ...itemForm, category_id: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="item-category-select">
                      <SelectValue placeholder="Kategori seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {categories.filter(c => !c.parent_id).map((cat) => (
                        <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {categories.filter(c => c.parent_id === itemForm.category_id).length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-slate-300">Alt Kategori</Label>
                    <select
                      value={itemForm.subcategory_id || ""}
                      onChange={(e) => setItemForm({ ...itemForm, subcategory_id: e.target.value })}
                      className="w-full rounded-md bg-slate-800 border-slate-700 text-white px-3 py-2 text-sm"
                      data-testid="item-subcategory-select"
                    >
                      <option value="">Alt kategori yok</option>
                      {categories.filter(c => c.parent_id === itemForm.category_id).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-slate-300">Ürün Adı</Label>
                  <Input
                    value={itemForm.name}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    placeholder="Örn: Izgara Köfte"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="item-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Kısa Açıklama</Label>
                  <Textarea
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    placeholder="Kısa ürün açıklaması..."
                    className="bg-slate-800 border-slate-700 text-white"
                    rows={2}
                    data-testid="item-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Detaylı Açıklama</Label>
                  <Textarea
                    value={itemForm.long_description || ""}
                    onChange={(e) => setItemForm({ ...itemForm, long_description: e.target.value })}
                    placeholder="Malzemeler, hazırlanış şekli, alerjen bilgisi vb."
                    className="bg-slate-800 border-slate-700 text-white"
                    rows={3}
                    data-testid="item-long-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Fiyat (TL)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={itemForm.price}
                    onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                    placeholder="0.00"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="item-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Ürün Görseli</Label>
                  {/* File Upload */}
                  <div className="flex gap-2">
                    <label className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-center gap-2 h-9 px-3 rounded-md bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors text-sm">
                        <Upload className="w-4 h-4" />
                        <span>{uploadingImage ? "Yükleniyor..." : "Fotoğraf Yükle"}</span>
                      </div>
                      <input type="file" accept="image/*" className="hidden" disabled={uploadingImage}
                        data-testid="item-image-file-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setUploadingImage(true);
                          try {
                            const formData = new FormData();
                            formData.append("file", file);
                            const res = await axios.post(`${API}/upload/image`, formData, {
                              headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                            });
                            setItemForm(prev => ({ ...prev, image_url: res.data.image_url }));
                            toast.success("Fotoğraf yüklendi!");
                          } catch (err) {
                            toast.error("Yükleme başarısız: " + (err.response?.data?.detail || "Hata"));
                          } finally {
                            setUploadingImage(false);
                            e.target.value = "";
                          }
                        }}
                      />
                    </label>
                  </div>
                  {/* URL input */}
                  <Input
                    value={itemForm.image_url}
                    onChange={(e) => setItemForm({ ...itemForm, image_url: e.target.value })}
                    placeholder="veya görsel URL'si yapıştırın..."
                    className="bg-slate-800 border-slate-700 text-white text-xs"
                    data-testid="item-image-input"
                  />
                  {/* Image preview */}
                  {itemForm.image_url && (
                    <div className="relative w-full h-24 rounded-lg overflow-hidden bg-slate-800 border border-slate-700">
                      <img src={itemForm.image_url.startsWith("/api") ? `${window.location.origin}${itemForm.image_url}` : itemForm.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      <button type="button" onClick={() => setItemForm(prev => ({ ...prev, image_url: "" }))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600">&times;</button>
                    </div>
                  )}
                  <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full border-purple-500/40 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200 gap-2"
                      disabled={aiImageLoading}
                      onClick={() => {
                        const catName = categories.find(c => c.id === itemForm.category_id)?.name || '';
                        generateAiImage(itemForm.name, itemForm.description, catName, (url) => setItemForm(prev => ({ ...prev, image_url: url })));
                      }}
                      data-testid="ai-generate-image-btn"
                    >
                      {aiImageLoading ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Oluşturuluyor...</>
                      ) : (
                        <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 0-4 4c0 2 2 3 2 6H6a2 2 0 0 0-2 2v2h16v-2a2 2 0 0 0-2-2h-4c0-3 2-4 2-6a4 4 0 0 0-4-4z"/><path d="M8 18v2a2 2 0 0 0 4 0v-2"/></svg> AI ile Görsel Oluştur</>
                      )}
                    </Button>
                  {itemForm.image_url && (
                    <div className="mt-2 rounded-lg overflow-hidden border border-slate-700 bg-slate-800">
                      <img src={itemForm.image_url} alt="Önizleme" className="w-full h-32 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                </div>
                <Button onClick={handleSaveItem} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="save-item-btn">
                  {editingItem ? "Güncelle" : "Oluştur"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Menu Photo Import Button */}
          <div>
            <input type="file" ref={menuPhotoRef} accept="image/*" className="hidden" onChange={handleMenuPhotoUpload}
              data-testid="menu-photo-input" />
            <Button onClick={() => menuPhotoRef.current?.click()}
              className="bg-violet-600 hover:bg-violet-700" data-testid="import-menu-btn">
              <ImageIcon className="w-4 h-4 mr-2" />
              Fotoğraftan Aktar
            </Button>
          </div>
          {/* Bulk AI Image Generation Button */}
          <Button
            onClick={bulkGenerateImages}
            disabled={bulkGenProgress !== null}
            className="bg-purple-600 hover:bg-purple-700 gap-2"
            data-testid="bulk-ai-generate-btn"
          >
            {bulkGenProgress ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {bulkGenProgress.current}/{bulkGenProgress.total} Oluşturuluyor...</>
            ) : (
              <><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 0-4 4c0 2 2 3 2 6H6a2 2 0 0 0-2 2v2h16v-2a2 2 0 0 0-2-2h-4c0-3 2-4 2-6a4 4 0 0 0-4-4z"/><path d="M8 18v2a2 2 0 0 0 4 0v-2"/></svg> Toplu AI Görsel</>
            )}
          </Button>
        </div>
      </div>

      {/* Menu Photo Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white">
              {importAnalyzing ? "Menü Analiz Ediliyor..." : importResults ? `${importResults.length} Ürün Bulundu` : "Menü İçe Aktar"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pt-2">
            {importAnalyzing && (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
                <p className="text-slate-400 text-sm">AI menü fotoğrafını analiz ediyor...</p>
                <p className="text-slate-500 text-xs">Bu birkaç saniye sürebilir</p>
              </div>
            )}
            {!importAnalyzing && importResults && importResults.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <ImageIcon className="w-12 h-12 text-slate-600" />
                <p className="text-slate-400">Menüde ürün bulunamadı.</p>
                <p className="text-slate-500 text-sm">Daha net bir fotoğraf deneyin.</p>
              </div>
            )}
            {!importAnalyzing && importResults && importResults.length > 0 && (
              <div className="space-y-2">
                <p className="text-slate-400 text-sm mb-3">Aşağıdaki ürünler algılandı. İstemediğiniz ürünü X ile kaldırabilirsiniz.</p>
                {importResults.map((item, idx) => (
                  <div key={idx} className="bg-slate-800 rounded-xl p-3 flex items-center gap-3 border border-slate-700"
                    data-testid={`import-item-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-violet-500/20 text-violet-300 text-[10px]">{item.category}</Badge>
                        <span className="text-white font-medium text-sm truncate">{item.name}</span>
                      </div>
                      {item.description && <p className="text-slate-400 text-xs mt-1 truncate">{item.description}</p>}
                    </div>
                    <span className="text-emerald-400 font-bold text-sm whitespace-nowrap">{Number(item.price).toFixed(2)} TL</span>
                    <button onClick={() => removeImportItem(idx)}
                      className="w-7 h-7 rounded-lg bg-slate-700 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                      data-testid={`remove-import-${idx}`}>
                      <X className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {!importAnalyzing && importResults && importResults.length > 0 && (
            <div className="pt-3 border-t border-slate-800">
              <Button onClick={handleBulkImport} disabled={importSubmitting}
                className="w-full bg-violet-600 hover:bg-violet-700" data-testid="confirm-import-btn">
                {importSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {importSubmitting ? "İçe Aktarılıyor..." : `${importResults.length} Ürünü İçe Aktar`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Categories and Items */}
      <div className="space-y-6">
        {categories.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 border-dashed">
            <CardContent className="py-12 text-center">
              <UtensilsCrossed className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Henüz kategori eklenmemiş.</p>
              <p className="text-slate-500 text-sm mt-1">Yukarıdaki "Kategori Ekle" butonunu kullanın.</p>
            </CardContent>
          </Card>
        ) : (
          categories.map((category) => (
            <Card key={category.id} className="bg-slate-900 border-slate-800" data-testid={`category-${category.id}`}>
              <CardHeader className="flex flex-row items-center justify-between py-4">
                <CardTitle className="text-white text-lg">{category.name}</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => openEditCategory(category)} className="text-slate-400 hover:text-white">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteCategory(category.id)} className="text-slate-400 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.filter(i => i.category_id === category.id).map((item) => (
                    <div key={item.id} className={`bg-slate-800 rounded-xl p-4 flex gap-4 ${item.visible === false ? 'opacity-50' : ''}`} data-testid={`item-${item.id}`}>
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="w-20 h-20 rounded-lg object-cover" />
                      ) : (
                        <button
                          className="w-20 h-20 rounded-lg bg-slate-700 flex flex-col items-center justify-center gap-1 hover:bg-purple-900/30 hover:border-purple-500/40 border border-transparent transition-all cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                          disabled={aiImageLoading}
                          onClick={() => {
                            const catName = category.name || '';
                            generateAiImage(item.name, item.description, catName, async (dataUrl) => {
                              try {
                                await axios.put(`${API}/menu/items/${item.id}`, { ...item, image_url: dataUrl, price: item.price }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                fetchData();
                              } catch { toast.error("Görsel kaydedilemedi"); }
                            });
                          }}
                          data-testid={`ai-gen-inline-${item.id}`}
                        >
                          {aiImageLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-400"><path d="M12 2a4 4 0 0 0-4 4c0 2 2 3 2 6H6a2 2 0 0 0-2 2v2h16v-2a2 2 0 0 0-2-2h-4c0-3 2-4 2-6a4 4 0 0 0-4-4z"/><path d="M8 18v2a2 2 0 0 0 4 0v-2"/></svg>
                              <span className="text-[9px] text-purple-400 font-medium">AI Oluştur</span>
                            </>
                          )}
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-white font-medium truncate">{item.name}</h4>
                          {item.visible === false && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">Gizli</span>
                          )}
                        </div>
                        <p className="text-slate-400 text-sm truncate">{item.description || "Açıklama yok"}</p>
                        <p className="text-emerald-400 font-semibold mt-1">{item.price.toFixed(2)} TL</p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleToggleVisibility(item.id)}
                          className={`p-2 ${item.visible === false ? 'text-red-400 hover:text-red-300' : 'text-slate-400 hover:text-white'}`}
                          title={item.visible === false ? "Görünür yap" : "Gizle"}
                          data-testid={`toggle-visibility-${item.id}`}>
                          {item.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditItem(item)} className="text-slate-400 hover:text-white p-2">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(item.id)} className="text-slate-400 hover:text-red-400 p-2">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {items.filter(i => i.category_id === category.id).length === 0 && (
                    <p className="text-slate-500 col-span-full text-center py-4">Bu kategoride ürün yok</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Order Management Component
const OrderManagement = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error("Siparişler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sipariş durumu güncellendi");
      fetchOrders();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-orange-500/20 text-orange-400",
      completed: "bg-emerald-500/20 text-emerald-400"
    };
    const labels = {
      pending: "Bekliyor",
      completed: "Tamamlandı"
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="order-management">
      <div>
        <h1 className="text-3xl font-bold text-white font-['Manrope']">Siparişler</h1>
        <p className="text-slate-400 mt-1">Gelen siparişleri buradan takip edin.</p>
      </div>

      {orders.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800 border-dashed">
          <CardContent className="py-12 text-center">
            <ShoppingBag className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Henüz sipariş yok.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} className={`bg-slate-900 border-slate-800 ${order.status === 'pending' ? 'ring-2 ring-orange-500/50' : ''}`} data-testid={`order-${order.id}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    {/* Table Number Badge - Prominent */}
                    {order.table_number && (
                      <div className="flex flex-col items-center justify-center bg-orange-500 text-white rounded-xl px-4 py-2 min-w-[70px]">
                        <span className="text-xs font-medium opacity-80">MASA</span>
                        <span className="text-2xl font-bold">{order.table_number}</span>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-white font-semibold">Sipariş #{order.id.slice(0, 8)}</h3>
                        {getStatusBadge(order.status)}
                      </div>
                      <p className="text-slate-400 text-sm mt-1">
                        {new Date(order.created_at).toLocaleString("tr-TR")}
                      </p>
                    </div>
                  </div>
                  <p className="text-emerald-400 text-2xl font-bold">{order.total.toFixed(2)} TL</p>
                </div>
                
                <div className="bg-slate-800 rounded-lg p-4 mb-4">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-1">
                      <span className="text-slate-300">{item.quantity}x {item.name}</span>
                      <span className="text-slate-400">{(item.quantity * item.price).toFixed(2)} TL</span>
                    </div>
                  ))}
                  {order.notes && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      <div className="flex items-start gap-2 bg-yellow-500/10 rounded-lg p-3">
                        <span className="text-yellow-500 text-lg">📝</span>
                        <div>
                          <p className="text-yellow-400 text-xs font-medium mb-1">MÜŞTERİ NOTU</p>
                          <p className="text-slate-300">{order.notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {order.status === "pending" && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => updateStatus(order.id, "completed")}
                      className="bg-emerald-500 hover:bg-emerald-600"
                      data-testid={`complete-order-${order.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" /> Tamamla
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Statistics Page ──────────────────────────────────────
const StatisticsPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("daily");

  const fetchStats = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/statistics?period=${p}`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch { toast.error("İstatistikler yüklenemedi"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchStats(period); }, [period, fetchStats]);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/statistics/export?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `istatistikler_${period}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("İstatistikler indirildi!");
    } catch { toast.error("İndirme başarısız"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  if (!stats) return <p className="text-slate-400">Veri bulunamadı</p>;

  const maxQty = Math.max(...(stats.top_items?.map(i => i.quantity) || [1]));
  const periodLabels = { daily: "Günlük (Son 7 Gün)", weekly: "Haftalık (Son 4 Hafta)", yearly: "Yıllık" };

  return (
    <div className="space-y-8" data-testid="statistics-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Manrope']">İstatistikler</h1>
          <p className="text-slate-400 mt-1">Satış ve sipariş verileri</p>
        </div>
        <div className="flex items-center gap-2">
          {["daily", "weekly", "yearly"].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${period === p ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'}`}
              data-testid={`period-${p}`}>
              {p === "daily" ? "Günlük" : p === "weekly" ? "Haftalık" : "Yıllık"}
            </button>
          ))}
          <Button onClick={handleExport} variant="outline" size="sm" className="border-slate-700 text-slate-400 hover:text-white ml-2" data-testid="export-stats-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            CSV İndir
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Toplam Sipariş", value: stats.total_orders, icon: <ShoppingBag className="w-5 h-5" />, color: "text-blue-400", bg: "bg-blue-500/10" },
          { label: "Toplam Gelir", value: `${stats.total_revenue.toFixed(0)} TL`, icon: <TrendingUp className="w-5 h-5" />, color: "text-emerald-400", bg: "bg-emerald-500/10" },
          { label: "Bekleyen", value: stats.pending_orders, icon: <Loader2 className="w-5 h-5" />, color: "text-amber-400", bg: "bg-amber-500/10" },
          { label: "Tamamlanan", value: stats.completed_orders, icon: <Check className="w-5 h-5" />, color: "text-green-400", bg: "bg-green-500/10" },
        ].map((card) => (
          <Card key={card.label} className="bg-slate-900 border-slate-800" data-testid={`stat-card-${card.label.toLowerCase().replace(/\s/g,'-')}`}>
            <CardContent className="p-4 md:p-5">
              <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center ${card.color} mb-3`}>{card.icon}</div>
              <p className="text-xl md:text-2xl font-bold text-white">{card.value}</p>
              <p className="text-slate-400 text-xs md:text-sm">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white">{periodLabels[period]}</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 md:gap-3 h-48 overflow-x-auto" data-testid="daily-chart">
            {stats.daily_stats?.map((day) => {
              const maxRev = Math.max(...(stats.daily_stats?.map(d => d.revenue) || [1]));
              const h = maxRev > 0 ? (day.revenue / maxRev) * 100 : 0;
              return (
                <div key={day.date} className="flex-1 min-w-[40px] flex flex-col items-center gap-1">
                  <span className="text-emerald-400 text-[10px] font-bold">{day.revenue > 0 ? `${day.revenue.toFixed(0)}₺` : ""}</span>
                  <div className="w-full bg-emerald-500/20 rounded-t-lg relative" style={{ height: `${Math.max(h, 4)}%` }}>
                    <div className="absolute inset-0 bg-emerald-500 rounded-t-lg" style={{ opacity: 0.7 + (h / 300) }} />
                  </div>
                  <span className="text-slate-500 text-[9px]">{day.date.slice(5)}</span>
                  <span className="text-slate-600 text-[8px]">{day.orders} sip.</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white">En Çok Satan Ürünler</CardTitle></CardHeader>
        <CardContent>
          {stats.top_items?.length === 0 ? (
            <p className="text-slate-500 text-sm">Henüz sipariş verisi yok</p>
          ) : (
            <div className="space-y-3" data-testid="top-items-list">
              {stats.top_items?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3" data-testid={`top-item-${idx}`}>
                  <span className="text-slate-600 text-sm font-bold w-6">{idx + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-medium">{item.name}</span>
                      <span className="text-slate-400 text-xs">{item.quantity} adet · {item.revenue.toFixed(0)} TL</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
                        style={{ width: `${(item.quantity / maxQty) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


const PREMIUM_THEMES = [
  { id: "warm-elegance", name: "Sıcak Elegance", primary: "#c8956c", secondary: "#d4a574", bg: "#faf6f1", accent: "#8b6f47", desc: "Sıcak toprak tonları" },
  { id: "marble-luxe", name: "Mermer Lüks", primary: "#1a1a2e", secondary: "#e2c799", bg: "#f5f0eb", accent: "#c9a96e", desc: "Mermer & altın" },
  { id: "rose-gold", name: "Rose Gold", primary: "#b76e79", secondary: "#e8c4c8", bg: "#fdf6f7", accent: "#d4a07a", desc: "Zarif pembe tonlar" },
  { id: "olive-garden", name: "Zeytin Bahçesi", primary: "#5c6b3c", secondary: "#a3b18a", bg: "#f7f5f0", accent: "#8b7355", desc: "Doğal yeşil tonlar" },
  { id: "ocean-breeze", name: "Okyanus Esintisi", primary: "#2c5f7c", secondary: "#6ba3be", bg: "#f0f6fa", accent: "#3d8db5", desc: "Mavi huzur" },
  { id: "noir-premium", name: "Noir Premium", primary: "#2d2d2d", secondary: "#c9b99a", bg: "#f8f6f3", accent: "#8b7d6b", desc: "Siyah & bej" },
  { id: "burgundy-royal", name: "Bordo Kraliyet", primary: "#722f37", secondary: "#c19a6b", bg: "#faf5f0", accent: "#8b4049", desc: "Asalet tonları" },
  { id: "lavender-mist", name: "Lavanta Sisi", primary: "#6b5b95", secondary: "#b8a9d4", bg: "#f8f5fd", accent: "#9b8ec4", desc: "Huzurlu mor tonlar" },
];

const HEADING_FONTS = [
  "Playfair Display", "Poppins", "Montserrat", "Raleway", "Oswald",
  "Manrope", "Lora", "Bitter", "Space Grotesk", "DM Sans"
];

const SUBHEADING_FONTS = [
  "Inter", "Open Sans", "Roboto", "Lato", "Quicksand",
  "Source Sans 3", "Nunito", "Manrope", "DM Sans", "Poppins"
];

const DESCRIPTION_FONTS = [
  "Inter", "Open Sans", "Roboto", "Lato", "Quicksand",
  "Source Sans 3", "Nunito", "DM Sans", "Poppins", "Manrope"
];

const RestaurantSettings = () => {
  const { token, restaurant, updateRestaurant } = useAuth();
  const defaultFeatures = { wifi_visible: true, google_review: true, bill_request: true, waiter_call: true, ai_assistant: true };
  const [form, setForm] = useState({
    name: restaurant?.name || "",
    logo_url: restaurant?.logo_url || "",
    primary_color: restaurant?.primary_color || "#c8956c",
    secondary_color: restaurant?.secondary_color || "#d4a574",
    background_color: restaurant?.background_color || "#faf6f1",
    heading_font: restaurant?.heading_font || "Playfair Display",
    subheading_font: restaurant?.subheading_font || "Inter",
    description_font: restaurant?.description_font || "Inter",
    custom_heading_font_url: restaurant?.custom_heading_font_url || "",
    custom_subheading_font_url: restaurant?.custom_subheading_font_url || "",
    custom_description_font_url: restaurant?.custom_description_font_url || "",
    heading_alignment: restaurant?.heading_alignment || "center",
    theme: restaurant?.theme || "warm-elegance",
    wifi_password: restaurant?.wifi_password || "",
    google_business_url: restaurant?.google_business_url || "",
    features: { ...defaultFeatures, ...(restaurant?.features || {}) },
  });
  const [saving, setSaving] = useState(false);
  const [uploadingFont, setUploadingFont] = useState(null);

  useEffect(() => {
    if (restaurant) {
      setForm({
        name: restaurant.name || "",
        logo_url: restaurant.logo_url || "",
        primary_color: restaurant.primary_color || "#c8956c",
        secondary_color: restaurant.secondary_color || "#d4a574",
        background_color: restaurant.background_color || "#faf6f1",
        heading_font: restaurant.heading_font || "Playfair Display",
        subheading_font: restaurant.subheading_font || "Inter",
        description_font: restaurant.description_font || "Inter",
        custom_heading_font_url: restaurant.custom_heading_font_url || "",
        custom_subheading_font_url: restaurant.custom_subheading_font_url || "",
        custom_description_font_url: restaurant.custom_description_font_url || "",
        heading_alignment: restaurant.heading_alignment || "center",
        theme: restaurant.theme || "warm-elegance",
        wifi_password: restaurant.wifi_password || "",
        google_business_url: restaurant.google_business_url || "",
        features: { ...defaultFeatures, ...(restaurant.features || {}) },
      });
    }
  }, [restaurant]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/restaurant/settings`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateRestaurant(response.data);
      toast.success("Ayarlar kaydedildi");
    } catch (error) {
      toast.error("Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleFontUpload = async (e, fontType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFont(fontType);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/font`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      const urlKey = `custom_${fontType}_font_url`;
      const fontKey = `${fontType}_font`;
      setForm(prev => ({ ...prev, [urlKey]: res.data.url, [fontKey]: file.name.replace(/\.[^.]+$/, "") }));
      toast.success(`${file.name} yüklendi!`);
    } catch {
      toast.error("Font yükleme başarısız");
    } finally {
      setUploadingFont(null);
    }
    e.target.value = "";
  };

  const applyTheme = (theme) => {
    setForm(prev => ({
      ...prev,
      theme: theme.id,
      primary_color: theme.primary,
      secondary_color: theme.secondary,
      background_color: theme.bg,
    }));
  };

  const isDark = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) < 128;
  };

  return (
    <div className="space-y-8" data-testid="restaurant-settings">
      <div>
        <h1 className="text-3xl font-bold text-white font-['Manrope']">Ayarlar</h1>
        <p className="text-slate-400 mt-1">Restoranınızın görünümünü özelleştirin.</p>
      </div>

      {/* General Info */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Genel Bilgiler</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-slate-300">Restoran Adı</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="settings-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Logo URL</Label>
            <Input
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://..."
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="settings-logo-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Heading Alignment */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Başlık Hizalama</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-sm mb-3">Kategori başlıklarının hizalamasını seçin</p>
          <div className="flex gap-3" data-testid="heading-alignment-picker">
            {[
              { value: "left", label: "Sol" },
              { value: "center", label: "Orta" },
              { value: "right", label: "Sağ" },
            ].map(({ value, label }) => (
              <button key={value} onClick={() => setForm({ ...form, heading_alignment: value })}
                className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all border ${form.heading_alignment === value ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500"}`}
                style={{ textAlign: value }}
                data-testid={`heading-align-${value}`}>
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Premium Themes */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Premium Tema Seçimi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="theme-picker">
            {PREMIUM_THEMES.map((theme) => {
              const isSelected = form.theme === theme.id;
              return (
                <button key={theme.id} onClick={() => applyTheme(theme)}
                  className={`relative rounded-xl overflow-hidden border-2 transition-all hover:scale-[1.03] ${isSelected ? "border-emerald-500 ring-2 ring-emerald-500/30" : "border-slate-700 hover:border-slate-500"}`}
                  data-testid={`theme-${theme.id}`}>
                  <div className="h-20 flex items-center justify-center gap-2 px-3" style={{ background: theme.bg }}>
                    <div className="w-8 h-8 rounded-full shadow-lg" style={{ background: theme.primary }} />
                    <div className="w-6 h-6 rounded-full shadow-md" style={{ background: theme.secondary }} />
                    <div className="w-4 h-4 rounded-full" style={{ background: theme.accent }} />
                  </div>
                  <div className="bg-slate-800 px-3 py-2">
                    <span className="text-xs text-slate-300 font-medium block">{theme.name}</span>
                    <span className="text-[9px] text-slate-500">{theme.desc}</span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-emerald-400 absolute top-2 right-2" />}
                  </div>
                </button>
              );
            })}
          </div>
          {/* Manual color fine-tuning */}
          <div className="mt-6">
            <Label className="text-slate-400 text-xs mb-3 block">Veya renkleri manuel ayarlayın</Label>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Ana Renk", key: "primary_color", testid: "settings-primary-color" },
                { label: "İkincil Renk", key: "secondary_color", testid: "settings-secondary-color" },
                { label: "Arka Plan", key: "background_color", testid: "settings-bg-color" },
              ].map(({ label, key, testid }) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-slate-400 text-xs">{label}</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border border-slate-700"
                      data-testid={testid} />
                    <span className="text-slate-500 text-xs font-mono">{form[key]}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WiFi & Google Business */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">WiFi & Google İşletme</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">WiFi Şifresi</Label>
            <Input value={form.wifi_password} onChange={(e) => setForm({ ...form, wifi_password: e.target.value })}
              placeholder="WiFi şifrenizi girin..." className="bg-slate-800 border-slate-700 text-white" data-testid="settings-wifi" />
            <p className="text-slate-500 text-xs">Müşteriler menüden WiFi şifresini görebilecek</p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Google İşletme URL</Label>
            <Input value={form.google_business_url} onChange={(e) => setForm({ ...form, google_business_url: e.target.value })}
              placeholder="https://g.page/..." className="bg-slate-800 border-slate-700 text-white" data-testid="settings-google-url" />
            <p className="text-slate-500 text-xs">Müşteriler bu bağlantıdan işletmenizi Google'da puanlayabilir</p>
          </div>
        </CardContent>
      </Card>

      {/* Feature Toggles */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Özellik Yönetimi</CardTitle>
          <p className="text-slate-400 text-sm">Müşteri menüsünde hangi özelliklerin görüneceğini seçin</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3" data-testid="feature-toggles">
            {[
              { key: "wifi_visible", label: "WiFi Şifresi Göster", desc: "Müşteriler WiFi şifresini görebilir" },
              { key: "google_review", label: "Google Yorum Butonu", desc: "Google işletme puanlama butonu" },
              { key: "bill_request", label: "Hesap İsteme", desc: "Müşteriler hesap isteyebilir" },
              { key: "waiter_call", label: "Garson Çağırma", desc: "Müşteriler garson çağırabilir" },
              { key: "ai_assistant", label: "AI Asistan", desc: "Sesli/yazılı AI sipariş asistanı" },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between p-3 rounded-xl bg-slate-800 border border-slate-700" data-testid={`toggle-${key}`}>
                <div>
                  <p className="text-white text-sm font-medium">{label}</p>
                  <p className="text-slate-500 text-xs">{desc}</p>
                </div>
                <button onClick={() => setForm(prev => ({ ...prev, features: { ...prev.features, [key]: !prev.features[key] } }))}
                  className={`w-11 h-6 rounded-full transition-all relative ${form.features[key] ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-all ${form.features[key] ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Font Selection */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Yazı Tipleri</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Heading Font */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-slate-300">Başlık Fontu</Label>
              <label className="cursor-pointer">
                <input type="file" accept=".ttf,.woff,.woff2,.otf" className="hidden" onChange={(e) => handleFontUpload(e, "heading")}
                  data-testid="upload-heading-font" />
                <span className={`text-xs px-3 py-1.5 rounded-lg border transition-all inline-flex items-center gap-1.5 ${uploadingFont === "heading" ? "border-slate-600 text-slate-500" : "border-slate-600 text-slate-400 hover:border-emerald-500 hover:text-emerald-400"}`}>
                  {uploadingFont === "heading" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Font Yükle
                </span>
              </label>
            </div>
            {form.custom_heading_font_url && (
              <div className="mb-2 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-300 text-xs">Özel font yüklendi: {form.heading_font}</span>
                <button onClick={() => setForm(prev => ({ ...prev, custom_heading_font_url: "", heading_font: "Playfair Display" }))}
                  className="ml-auto text-slate-400 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="heading-font-picker">
              {HEADING_FONTS.map((font) => (
                <button key={font} onClick={() => setForm({ ...form, heading_font: font, custom_heading_font_url: "" })}
                  className={`rounded-xl px-3 py-3 text-center transition-all border ${form.heading_font === font && !form.custom_heading_font_url ? "border-emerald-500 bg-emerald-500/10 text-white" : "border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500 hover:text-slate-200"}`}
                  data-testid={`heading-font-${font.replace(/\s/g, '-').toLowerCase()}`}>
                  <span className="text-lg font-semibold block" style={{ fontFamily: `'${font}', serif` }}>Aa</span>
                  <span className="text-[10px] mt-1 block opacity-70">{font}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Subheading Font */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-slate-300">Alt Başlık Fontu</Label>
              <label className="cursor-pointer">
                <input type="file" accept=".ttf,.woff,.woff2,.otf" className="hidden" onChange={(e) => handleFontUpload(e, "subheading")}
                  data-testid="upload-subheading-font" />
                <span className={`text-xs px-3 py-1.5 rounded-lg border transition-all inline-flex items-center gap-1.5 ${uploadingFont === "subheading" ? "border-slate-600 text-slate-500" : "border-slate-600 text-slate-400 hover:border-emerald-500 hover:text-emerald-400"}`}>
                  {uploadingFont === "subheading" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Font Yükle
                </span>
              </label>
            </div>
            {form.custom_subheading_font_url && (
              <div className="mb-2 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-300 text-xs">Özel font yüklendi: {form.subheading_font}</span>
                <button onClick={() => setForm(prev => ({ ...prev, custom_subheading_font_url: "", subheading_font: "Inter" }))}
                  className="ml-auto text-slate-400 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="subheading-font-picker">
              {SUBHEADING_FONTS.map((font) => (
                <button key={font} onClick={() => setForm({ ...form, subheading_font: font, custom_subheading_font_url: "" })}
                  className={`rounded-xl px-3 py-3 text-center transition-all border ${form.subheading_font === font && !form.custom_subheading_font_url ? "border-emerald-500 bg-emerald-500/10 text-white" : "border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500 hover:text-slate-200"}`}
                  data-testid={`subheading-font-${font.replace(/\s/g, '-').toLowerCase()}`}>
                  <span className="text-base block" style={{ fontFamily: `'${font}', sans-serif` }}>Aa</span>
                  <span className="text-[10px] mt-1 block opacity-70">{font}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Description Font */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-slate-300">Açıklama Fontu</Label>
              <label className="cursor-pointer">
                <input type="file" accept=".ttf,.woff,.woff2,.otf" className="hidden" onChange={(e) => handleFontUpload(e, "description")}
                  data-testid="upload-description-font" />
                <span className={`text-xs px-3 py-1.5 rounded-lg border transition-all inline-flex items-center gap-1.5 ${uploadingFont === "description" ? "border-slate-600 text-slate-500" : "border-slate-600 text-slate-400 hover:border-emerald-500 hover:text-emerald-400"}`}>
                  {uploadingFont === "description" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Font Yükle
                </span>
              </label>
            </div>
            {form.custom_description_font_url && (
              <div className="mb-2 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-300 text-xs">Özel font yüklendi: {form.description_font}</span>
                <button onClick={() => setForm(prev => ({ ...prev, custom_description_font_url: "", description_font: "Inter" }))}
                  className="ml-auto text-slate-400 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="description-font-picker">
              {DESCRIPTION_FONTS.map((font) => (
                <button key={font} onClick={() => setForm({ ...form, description_font: font, custom_description_font_url: "" })}
                  className={`rounded-xl px-3 py-3 text-center transition-all border ${form.description_font === font && !form.custom_description_font_url ? "border-emerald-500 bg-emerald-500/10 text-white" : "border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500 hover:text-slate-200"}`}
                  data-testid={`description-font-${font.replace(/\s/g, '-').toLowerCase()}`}>
                  <span className="text-base block" style={{ fontFamily: `'${font}', sans-serif` }}>Aa</span>
                  <span className="text-[10px] mt-1 block opacity-70">{font}</span>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Önizleme</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-2xl p-6 overflow-hidden" style={{ backgroundColor: form.background_color }} data-testid="settings-preview">
            <div className="flex items-center gap-4 mb-5">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo" className="w-12 h-12 rounded-xl object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: form.primary_color }}>
                  <ChefHat className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h3 className="text-xl font-bold" style={{ color: form.primary_color, fontFamily: `'${form.heading_font}', serif` }}>{form.name || "Restoran Adı"}</h3>
                <p className="text-sm" style={{ color: isDark(form.background_color) ? "#9ca3af" : "#6b7280", fontFamily: `'${form.subheading_font}', sans-serif` }}>Alt başlık örneği</p>
              </div>
            </div>
            <div className="rounded-xl p-4 border" style={{ borderColor: isDark(form.background_color) ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)", backgroundColor: isDark(form.background_color) ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.8)" }}>
              <p className="font-semibold mb-1" style={{ color: isDark(form.background_color) ? "#fff" : "#1f2937", fontFamily: `'${form.heading_font}', serif` }}>Örnek Yemek Adı</p>
              <p className="text-sm mb-1" style={{ color: isDark(form.background_color) ? "#9ca3af" : "#6b7280", fontFamily: `'${form.subheading_font}', sans-serif` }}>Alt başlık metni</p>
              <p className="text-xs" style={{ color: isDark(form.background_color) ? "#6b7280" : "#9ca3af", fontFamily: `'${form.description_font}', sans-serif` }}>Açıklama fontu ile yazılmış detaylı açıklama metni</p>
              <p className="font-bold mt-2" style={{ color: form.primary_color, fontFamily: `'${form.subheading_font}', sans-serif` }}>49.90 TL</p>
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="mt-6 bg-emerald-500 hover:bg-emerald-600" data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Kaydet
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
